import type { ArticleShort } from "lib/types/article";
import type { Author } from "lib/types/article";

import ProTag from "components/proTag/proTag";
import { AuthorsLinkList } from "components/authorsLinkList/authorsLinkList";

type MixPanelData = {
  title: string;
  [key: string]: any;
};

type TagPill = {
  name: string;
  url: string;
};

export type ArticleListCardProps = Pick<
  ArticleShort,
  | "id"
  | "title"
  | "isProContent"
  | "excerpt"
  | "publishTimestamp"
  | "authors"
  | "url"
  | "sponsorDetails"
  | "isSponsored"
  | "category"
  | "sponsorshipType"
> & {
  linkLabel?: string;
  onTrackingEventClicked?: (data: MixPanelData) => Promise<void>;
  /**
   * Shows tag chips instead of author
   */
  tagPills?: TagPill[];
};

export const ArticleListCard = ({
  id,
  linkLabel,
  title,
  category,
  isProContent,
  excerpt,
  publishTimestamp,
  authors,
  url,
  isSponsored,
  sponsorDetails,
  onTrackingEventClicked,
  sponsorshipType,
  tagPills,
}: ArticleListCardProps) => {
  const isWithPills = tagPills && tagPills.length > 0;

  const onTrackAuthorClicked = async (author: Author, link: string) => {
    if (onTrackingEventClicked) {
      await onTrackingEventClicked({
        title: "Authors click",
        "Click link": link,
        "Click text": author.name,
      });
    }
  };

  const onTrackClick = async (data: MixPanelData) => {
    if (onTrackingEventClicked) {
      await onTrackingEventClicked(data);
    }
  };

  return (
    <div className={"relative"} data-testid={`articleListCard-${id}`}>
      <a
        aria-label={linkLabel ? `${linkLabel} ${title}` : undefined}
        href={url}
        className={
          "ga-article-list-card articleListCard__link peer absolute inset-0 -z-0"
        }
        onClick={async () =>
          await onTrackClick({
            title: "Article tag list click",
            "Click link": url,
            "Click text": title,
          })
        }
      >
        <span
          className={"sr-only"}
          dangerouslySetInnerHTML={{
            __html: title,
          }}
        ></span>
      </a>
      <div className={"py-4 peer-hover:bg-mono-10 md:pt-8"}>
        <div className={"mb-4 flex items-center md:min-h-[32px]"}>
          {isProContent && (
            <div className="mr-1">
              <ProTag variant="pill" />
            </div>
          )}
          {category && category.url && (
            <a
              href={category.url}
              data-testid={"articleListCard__category"}
              className={
                "ga-article-list-card-category z-1 relative mr-2 font-bold text-[14px] leading-[120%] text-cerise transition-colors delay-150 hover:text-black md:mr-4"
              }
              onClick={async () =>
                await onTrackClick({
                  title: "Category click",
                  Component: "ArticleListCard",
                  "Click link": category.url,
                  "Click text": category.name,
                })
              }
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: category.name,
                }}
              />
            </a>
          )}
          {publishTimestamp && (
            <p
              className={
                "whitespace-nowrap text-[14px] leading-4 text-body-muted"
              }
            >
              {new Date(publishTimestamp).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </p>
          )}
        </div>
        <h2 className="articleListCard__title mb-4 font-recklessBold text-[18px] leading-[120%] transition-colors delay-150 md:text-[32px]">
          <span
            dangerouslySetInnerHTML={{
              __html: title,
            }}
          />
        </h2>
        {excerpt && (
          <p
            className={"description mb-4 hidden md:block"}
            data-testid={"articleListCard__excerpt"}
          >
            <span
              dangerouslySetInnerHTML={{
                __html: excerpt,
              }}
            />
          </p>
        )}
        {!isSponsored && authors && !isWithPills && (
          <div
            className={
              "flex flex-1 items-end font-bold text-[16px] leading-[100%] text-cerise transition-colors delay-150 hover:text-black md:mb-4 md:text-[14px]"
            }
          >
            <div className={"z-1 relative inline"}>
              <AuthorsLinkList
                authors={authors}
                onAuthorClick={onTrackAuthorClicked}
                gaClassName={"ga-article-list-author"}
              />
            </div>
          </div>
        )}
        {isSponsored && sponsorDetails && (
          <div
            className={
              "flex flex-1 items-end font-bold text-[16px] leading-[100%] text-cerise transition-colors delay-150 hover:text-black md:mb-4 md:text-[14px]"
            }
          >
            <div className={"z-1 relative inline"}>
              <a
                className={"ga-article-list-card-sponsor"}
                target="_blank"
                rel="noopener noreferrer"
                href={sponsorDetails.url || "#"}
                onClick={async () =>
                  await onTrackClick({
                    title: "Sponsor article list click",
                    "Click link": sponsorDetails.url,
                    "Click text": sponsorDetails.name,
                  })
                }
              >
                {sponsorshipType || "Sponsored by"}:{" "}
                {sponsorDetails.name && (
                  <span
                    dangerouslySetInnerHTML={{
                      __html: sponsorDetails.name,
                    }}
                  />
                )}
              </a>
            </div>
          </div>
        )}
        {isWithPills && (
          <div className={"z-1 relative mt-2"}>
            <div className="flex flex-wrap gap-x-2 gap-y-2">
              {tagPills.map((tag, i: number) => (
                <a
                  className={"ga-article-list-card-pill"}
                  onClick={async () =>
                    await onTrackClick({
                      title: "Article tag list pill click",
                      "Click link": tag.url,
                      "Click text": tag.name,
                    })
                  }
                  href={tag.url}
                  key={i}
                >
                  <span className="inline-block select-none rounded-full bg-mono-10 px-4 py-1 text-[16px] leading-[120%] text-black-rock transition-[background-color] duration-200 ease-in-out hover:bg-mono-15">
                    <span className="font-bold text-cerise">#</span>{" "}
                    <span dangerouslySetInnerHTML={{ __html: tag.name }} />
                  </span>
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
